'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import SplitType from 'split-type';
import { motion, useInView } from 'motion/react';

/**
 * Reveals a block of text word-by-word with a staggered rise.
 *
 * Uses SplitType to wrap each word in a span, then plays a small rise-fade
 * animation when the element scrolls into view. `as` lets callers pick the
 * semantic element (h1, h2, p) without nesting.
 *
 * Honors prefers-reduced-motion: renders plain text when requested.
 */
export function TextReveal({
  children,
  as: Tag = 'span',
  className = '',
  delay = 0,
  once = true,
  stagger: staggerMs = 32,
}: {
  children: ReactNode;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  delay?: number;
  once?: boolean;
  /** Per-word stagger in ms. */
  stagger?: number;
}) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once, amount: 0.35 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const split = new SplitType(el, {
      types: 'words',
      tagName: 'span',
    });

    const words = split.words ?? [];
    words.forEach((w, i) => {
      const span = w as HTMLElement;
      span.style.display = 'inline-block';
      span.style.willChange = 'transform, opacity';
      span.style.opacity = '0';
      span.style.transform = 'translateY(0.4em)';
      span.style.transition = `opacity 700ms cubic-bezier(0.16,1,0.3,1) ${delay + i * staggerMs}ms, transform 700ms cubic-bezier(0.16,1,0.3,1) ${delay + i * staggerMs}ms`;
    });

    return () => {
      split.revert();
    };
  }, [delay, staggerMs]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !inView) return;
    const words = el.querySelectorAll<HTMLElement>('.word');
    words.forEach((w) => {
      w.style.opacity = '1';
      w.style.transform = 'translateY(0)';
    });
  }, [inView]);

  const MotionTag = motion[Tag as keyof typeof motion] as typeof motion.span;

  return (
    <MotionTag ref={ref as React.Ref<HTMLSpanElement>} className={className}>
      {children}
    </MotionTag>
  );
}
